import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Typography,
	Stack,
	Card,
	Container,
	TextField,
	InputAdornment,
	IconButton
} from '@mui/material';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import { Icon } from '@iconify/react';
import * as Yup from 'yup';
import { updatePassword } from '../../api/auth';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { UserContext } from '../../Contexts/UserContext';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import MHidden from '../../Components/Mhidden';
import LoginSvg from '../../assets/svgs/login.svg';
import { useFormik, Form, FormikProvider } from 'formik';
import { isValidEmail } from '../../utilities/helpers';

const RootStyle = styled('div')(({ theme }) => ({
	[theme.breakpoints.up('md')]: {
		display: 'flex',
		height: '100vh',
		justifyContent: 'center',
		alignItems: 'center',
		width: '100%'
	}
}));

const SectionStyle = styled(Card)(({ theme }) => ({
	width: '100%',
	maxWidth: 464,
	maxHeight: '100vh',
	minHeight: '90vh',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	margin: theme.spacing(2, 0, 2, 2),
	boxShadow: 16
}));

const ContentStyle = styled('div')(({ theme }) => ({
	maxWidth: 480,
	margin: 'auto',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	padding: theme.spacing(12, 0),
	color: theme.palette.primary.main
}));

const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
	borderRadius: '8px'
}));

export default function NewPasswordPage() {
	return (
		<RootStyle>
			<MHidden width="mdDown">
				<SectionStyle>
					<img src={LoginSvg} alt="login" />
				</SectionStyle>
			</MHidden>

			<Container>
				<ContentStyle>
					<Stack sx={{ mb: 5 }}>
						<Typography variant="h4" gutterBottom fontWeight="bold">
							Set New Password
						</Typography>
						<Typography sx={{ color: 'text.secondary' }}>Enter Password below to reset.</Typography>
					</Stack>

					<NewPasswordForm />
				</ContentStyle>
			</Container>
		</RootStyle>
	);
}

function NewPasswordForm() {
	const navigate = useNavigate();
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [searchParams] = useSearchParams();
	const email = searchParams.get('email');

	const NewPasswordFormSchema = Yup.object().shape({
		password: Yup.string().min(3).max(15).required('Password is required'),
		confirmPassword: Yup.string()
			.oneOf([Yup.ref('password'), null], 'Passwords must match')
			.required('Confirm password is required')
	});

	const handleResetPassword = async (formData) => {
		try {
			if (!isValidEmail(email)) {
				toast.error('Email invalid! Something went wrong!');
			} else {
				await updatePassword({ email, password: formData.password });
				toast.success('Password Updated Successfully');
			}
			navigate('/login', { replace: true });
		} catch (err) {
			toast.error('Error Updating Password');
		}
	};

	const formik = useFormik({
		initialValues: {
			password: '',
			confirmPassword: ''
		},
		validationSchema: NewPasswordFormSchema,
		onSubmit: handleResetPassword
	});

	const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

	return (
		<FormikProvider value={formik}>
			<Form autoComplete="off" noValidate onSubmit={handleSubmit}>
				<Stack spacing={3}>
					<TextField
						fullWidth
						type={showPassword ? 'text' : 'password'}
						label="Password"
						{...getFieldProps('password')}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
										<Icon icon={showPassword ? eyeFill : eyeOffFill} />
									</IconButton>
								</InputAdornment>
							)
						}}
						error={Boolean(touched.password && errors.password)}
						helperText={touched.password && errors.password}
					/>

					<TextField
						fullWidth
						type={showConfirmPassword ? 'text' : 'password'}
						label="Confirm Password"
						{...getFieldProps('confirmPassword')}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton edge="end" onClick={() => setShowConfirmPassword((prev) => !prev)}>
										<Icon icon={showConfirmPassword ? eyeFill : eyeOffFill} />
									</IconButton>
								</InputAdornment>
							)
						}}
						error={Boolean(touched.confirmPassword && errors.confirmPassword)} // Fix: check confirmPassword error
						helperText={touched.confirmPassword && errors.confirmPassword} // Fix: show confirmPassword helper text
					/>

					<StyledLoadingButton
						fullWidth
						size="large"
						type="submit"
						variant="contained"
						loading={isSubmitting}
					>
						Reset Password
					</StyledLoadingButton>
				</Stack>
			</Form>
		</FormikProvider>
	);
}
